import React, { useMemo } from 'react';
import { Card } from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { Eye } from 'lucide-react';
import { SlideData, PresentationTheme } from '@dopomogai/types';
import { DynamicRenderer } from './DynamicRenderer';

interface PresentationPreviewProps {
  slides: SlideData[];
  activeSlideId: string | null;
  theme: PresentationTheme;
}

export const PresentationPreview: React.FC<PresentationPreviewProps> = ({
  slides,
  activeSlideId,
  theme,
}) => {
  const activeSlide = useMemo(() => slides.find(s => s.id === activeSlideId), [slides, activeSlideId]);

  const renderContent = () => {
    if (!activeSlide) {
      return (
         <div className="flex flex-col items-center justify-center h-full text-center text-slate-500">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <Eye size={32} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700">Live Preview</h3>
            <p className="max-w-xs">Select a slide from the panel on the left to see it here.</p>
        </div>
      );
    }
    
    return (
      <div className="w-full h-full p-8 bg-slate-200 flex items-center justify-center">
        <div 
            className="aspect-[16/9] w-full max-w-5xl bg-white rounded-lg shadow-2xl overflow-hidden relative"
            style={{ backgroundColor: theme.colors.tertiary }}
        >
          {/* We pass `data` and `theme` as props. The DynamicRenderer will forward them.*/}
          <DynamicRenderer
            code={activeSlide.content}
            data={activeSlide}
            theme={theme}
          />
        </div>
      </div>
    );
  };

  return (
    <Card
      title="Slide Preview"
      icon={<Eye size={20} />}
      className="h-full flex flex-col"
      noPadding
      noHoverEffect
    >
        <div className="flex-grow bg-slate-100">
            <motion.div
                key={activeSlideId || 'empty'}
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full"
            >
                {renderContent()}
            </motion.div>
        </div>
    </Card>
  );
};